/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/button-has-type */
import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import * as PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import {
  Select, Row, Input, Button, Form, Typography, Icon, Popover, notification, Alert
} from 'antd';
import { Link } from 'react-router-dom';
import FormItem from 'antd/lib/form/FormItem';
import ReactQuill from 'react-quill';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import '../my-details/MyDetails.css';
import ViaValidationAlert from '../../../components/fields/ViaValidationAlert';
import { requestSubmitSupportTicket } from '../../../state/ui/support/supportUiActions';
import { selectSubmitSupportTicketRequest } from '../../../state/ui/support/supportUiReducer';
import routeLocations from '../../../utility/route-locations';
import { selectSessionUser } from '../../../state/ui/application/sessionUiReducer';
import { requestSendVerificationEmail } from '../../../state/ui/configuration/users/emailUiActions';

const { Title, Text } = Typography;

// This breaks quill if it is re-rendered. So I pulled it out of the main component.
const modules = {
  toolbar: {
    container: '#toolbar',
  },
  clipboard: {
    matchVisual: false,
  },
};

/**
* This also cannot be re-created on render (it loses all of its functionality with quill, god knows why)
* Custom toolbar component to allow control of what buttons appear.
*/
const CustomToolbar = () => (
  <Row id="toolbar" type="flex" justify="space-around">
    <button className="ql-bold" />
    <button className="ql-italic" />
    <button className="ql-underline" />
    <button className="ql-list" value="ordered" />
    <button className="ql-list" value="bullet" />
  </Row>
);

// Not sure if this needs to be outside the component, but its similar to modules so I'm not risking it.
const formats = [
  'bold', 'italic', 'underline',
  'list', 'bullet', 'indent',
];

const TypeField = ({ form }) => {
  const intl = useIntl();
  return (
    <FormItem required label={intl.formatMessage({ id: 'ticketType' })}>
      {form.getFieldDecorator('ticketType', {
        initialValue: 'generalEnquiry',
        rules: [
          { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        ],
      })(
        <Select className="contact-us-field">
          <Select.Option value="generalEnquiry">{intl.formatMessage({ id: 'generalEnquiry' })}</Select.Option>
          <Select.Option value="purchases">{intl.formatMessage({ id: 'purchases' })}</Select.Option>
          <Select.Option value="configuration">{intl.formatMessage({ id: 'configuration' })}</Select.Option>
          <Select.Option value="accountAndUsers">{intl.formatMessage({ id: 'accountAndUsers' })}</Select.Option>
          <Select.Option value="accountDeletion">{intl.formatMessage({ id: 'accountDeletion' })}</Select.Option>
        </Select>,
      )}
    </FormItem>
  );
};

TypeField.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
  }).isRequired,
};

const DescriptorPopover = () => {
  const popoverContent = (
    <Row style={{ width: '200px' }}>
      <FormattedMessage id="transDescDesc" />
      &nbsp;
      <Link to={routeLocations.purchaseHistory}>
        <FormattedMessage id="goNow" />
        &nbsp;
        <Icon type="arrow-right" style={{ fontSize: '8pt', marginLeft: '0.2em', color: 'var(--component-primary-colour)' }} />
      </Link>
    </Row>
  );
  return (
    <span>
      <FormattedMessage id="purchaseID" />
      <Popover content={popoverContent} placement="rightTop">
        <Icon type="info-circle" theme="twoTone" style={{ marginLeft: '0.3em' }} />
      </Popover>
    </span>
  );
};

const DescriptorField = ({ form }) => {
  // Only relevant to purchase support requests
  const visible = form.getFieldValue('ticketType') === 'purchases';
  if (!visible) return <></>;

  return (
    <FormItem label={<DescriptorPopover />}>
      {form.getFieldDecorator('purchaseId', {})(
        <Input className="contact-us-field" />,
      )}
    </FormItem>
  );
};

DescriptorField.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
  }).isRequired,
};


const ContentField = ({ form }) => {
  // The quill instance contains all methods to interact with the text editor.
  // It isnt needed here since we don't actually interact withthe quill instance outside of the onChange method.
  const quillRef = useRef();
  const intl = useIntl();
  // The html produced by the editor. Allows programatic changing of the content with html (I.E restore default)
  // Here we are simply using it to update our form fields
  const [currentHtml, setCurrentHtml] = useState('');

  useEffect(() => {
    if (form.getFieldValue('content') !== currentHtml) form.setFieldsValue({ content: currentHtml });
  }, [currentHtml, form]);

  return (
    <FormItem required label={intl.formatMessage({ id: 'question' })}>
      {form.getFieldDecorator('content', {
        rules: [
          { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        ],
      })(
        <div>
          <CustomToolbar />
          <ReactQuill
            className="contact-us-field"
            style={{ margin: '0px', height: '200px' }}
            ref={quillRef}
            formats={formats}
            value={currentHtml}
            modules={modules}
            onChange={(content, delta, source, editor) => { setCurrentHtml(editor.getHTML()); }}
          />
        </div>,
      )}
    </FormItem>
  );
};

ContentField.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
  }).isRequired,
};

function ContactUs({ form,  match }) {
  const intl = useIntl();
  // Reason code can be passed in
  const ticketType = match && match.params ? match.params.ticketType : null;
  if (form.setFieldsValue && ticketType && !form.getFieldValue('ticketType'))
  {
    form.setFieldsValue({
      ticketType,
    });
  }
  // Redux
  const sessionUser = useSelector(selectSessionUser);
  const submitRequest = useSelector(selectSubmitSupportTicketRequest);
  const dispatch = useDispatch();
  const submitTicket = useCallback(ticket => dispatch(requestSubmitSupportTicket(ticket)), [dispatch]);
  const sendNewVerificationEmail = useCallback(() => dispatch(requestSendVerificationEmail()), [dispatch]);

  const UnverifiedNotificationDescription = useCallback(() => (
    <div>
      <Text>{ intl.formatMessage({ id: 'notVerifiedContactUs' })}</Text>
      <Button
        style={{ marginTop: '12px' }}
        type="primary"
        block
        onClick={() => { notification.close('resend-verification-email'); sendNewVerificationEmail(); }}
      >
        { intl.formatMessage({ id: 'verifyEmailResend' }) }
      </Button>
    </div>
  ), [sendNewVerificationEmail, intl]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (!sessionUser.emailVerified) {
        notification.warn({
          key: 'resend-verification-email',
          message: intl.formatMessage({ id: 'notVerified' }),
          description: <UnverifiedNotificationDescription />,
          duration: null,
        });
      } else if (!err) {
        submitTicket({ ...values });
      }
    });
  }, [form, submitTicket, intl, sessionUser]);


  return (
    <Form className="my-details-layout" onSubmit={e => handleSubmit(e)}>
      <Row type="flex" align="middle" justify="center">
        <div className="my-details-content">
          <Title level={2} style={{ textAlign: 'center' }}><FormattedMessage id="contactUs" /></Title>
          <ViaValidationAlert
            validationError={submitRequest ? submitRequest.error : null}
            className="contact-us-field"
            style={{ marginBottom: '0.6em' }}
          />
          <TypeField form={form} />
          {form.getFieldValue('ticketType') === 'accountDeletion' ? <Alert showIcon message={intl.formatMessage({ id: 'accountDeletionDescriptor' })} type="info"/> : <></>}
          <DescriptorField form={form} />
          <ContentField form={form} />
          <Row type="flex" justify="end">
            <Button type="primary" htmlType="submit" loading={submitRequest ? submitRequest.isLoading : false}><FormattedMessage id="submit" /></Button>
          </Row>
        </div>
      </Row>
    </Form>
  );
}

ContactUs.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
    validateFields: PropTypes.func.isRequired,
  }).isRequired,
};

export default withRouter(Form.create({ name: 'contact-us' })(ContactUs));
